import { createContext, useContext, useState, useEffect } from "react";
import { MealPlanApi } from "../api/MealPlanApi";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";

const MealPlanContext = createContext();

export const useMealPlan = () => {
  return useContext(MealPlanContext);
};

export const MealPlanProvider = ({ children }) => {
  const [mealPlan, setMealPlan] = useState([]);
  const [weekStartDate, setWeekStartDate] = useState(null);
  const [thisWeekFetched, setThisWeekFetched] = useState(false);

  const fetchMealPlan = async (date) => {
    if (!date) return;
    try {
      const response = await MealPlanApi.fetchMealPlan(date);
      setMealPlan(response.mealPlanEntries);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to fetch meal plan", error, { correlationId });
      toast.error(getErrorMessage(error));
    }
  };

  const fetchThisWeekMealPlan = async () => {
    try {
      const response = await MealPlanApi.fetchMealPlan();
      setMealPlan(response.mealPlanEntries);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to fetch meal plan", error, { correlationId });
      toast.error(getErrorMessage(error));
    }
  };

  const updateMealPlan = async (updateMealPlanRequest, dayIndex, mealIndex) => {
    const { leftoverMeals = [], updatedMeals = [] } = updateMealPlanRequest;

    const updatedEntries = updatedMeals.map((recipe) => ({
      entryId: crypto.randomUUID(),
      dayCode: dayIndex,
      mealCode: mealIndex,
      mealId: recipe.mealId,
      mealName: recipe.mealName,
      isLeftover: leftoverMeals.some((lm) => lm.mealId === recipe.mealId),
      ingredientMultiplier: recipe.ingredientMultiplier || 1,
    }));

    const previousEntries = mealPlan.filter(
      (entry) => entry.dayCode === dayIndex && entry.mealCode === mealIndex,
    );

    setMealPlan((prev) => {
      const filtered = prev.filter(
        (entry) =>
          !(entry.dayCode === dayIndex && entry.mealCode === mealIndex),
      );
      return [...filtered, ...updatedEntries];
    });

    const mealMultiplierMap = new Map();
    updatedMeals.forEach((meal) => {
      mealMultiplierMap.set(meal.mealId, meal.ingredientMultiplier || 1);
    });
    const updateRequest = {
      updatedMealIds: Object.fromEntries(mealMultiplierMap),
      leftoverMeals: leftoverMeals,
    };

    try {
      await MealPlanApi.updateMealPlan(
        updateRequest,
        dayIndex,
        mealIndex,
        weekStartDate,
      );
      const response = await MealPlanApi.fetchMealPlan(weekStartDate);
      setMealPlan(response.mealPlanEntries);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to update meal plan", error, {
        correlationId,
        dayIndex,
        mealIndex,
      });
      toast.error(getErrorMessage(error));
      setMealPlan((prev) => {
        const filtered = prev.filter(
          (entry) =>
            !(entry.dayCode === dayIndex && entry.mealCode === mealIndex),
        );
        return [...filtered, ...previousEntries];
      });
    }
  };

  const clearMealPlan = () => {
    const previousState = mealPlan;
    setMealPlan([]);
    MealPlanApi.clearMealPlan(weekStartDate).catch((error) => {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to clear meal plan", error, { correlationId });
      toast.error(getErrorMessage(error));
      setMealPlan(previousState);
    });
  };

  const clearDays = async (dayCodes) => {
    const previousState = mealPlan;
    setMealPlan((prev) =>
      prev.filter((entry) => !dayCodes.includes(entry.dayCode)),
    );
    try {
      await MealPlanApi.clearDays(dayCodes, weekStartDate);
      const response = await MealPlanApi.fetchMealPlan(weekStartDate);
      setMealPlan(response.mealPlanEntries);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      logger.error("Failed to clear days", error, { correlationId, dayCodes });
      toast.error(getErrorMessage(error));
      setMealPlan(previousState);
    }
  };

  const value = {
    mealPlan,
    weekStartDate,
    setWeekStartDate,
    fetchMealPlan,
    updateMealPlan,
    clearMealPlan,
    clearDays,
  };

  useEffect(() => {
    if (weekStartDate) fetchMealPlan(weekStartDate);
  }, [weekStartDate]);

  useEffect(() => {
    if (!thisWeekFetched) {
      fetchThisWeekMealPlan();
      setThisWeekFetched(true);
    }
  }, [thisWeekFetched]);

  return (
    <MealPlanContext.Provider value={value}>
      {children}
    </MealPlanContext.Provider>
  );
};
